import { Component, computed, DestroyRef, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { BreadcrumbNavigationService } from '@angular-monorepo/ui';
import { Breadcrumb } from 'primeng/breadcrumb';
import { MenuItem, PrimeTemplate } from 'primeng/api';
import { CardModule } from 'primeng/card';
import { ChartModule } from 'primeng/chart';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Button } from 'primeng/button';
import { Select } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { Menu } from 'primeng/menu';
import { Tooltip } from 'primeng/tooltip';
import { DashboardSummaryResponse, WorkflowOption } from '../../interfaces';
import { DashboardService } from '../../services';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'lib-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    Breadcrumb,
    PrimeTemplate,
    CardModule,
    ChartModule,
    ProgressSpinnerModule,
    Button,
    Select,
    TableModule,
    TranslatePipe,
    Tooltip,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css',
})
export class DashboardComponent implements OnInit, OnDestroy {
  readonly dashboardData = signal<DashboardSummaryResponse | null>(null);
  readonly loading = signal(true);
  readonly errorMessage = signal('');
  readonly volumeChartData = signal<{
    labels: string[];
    datasets: Array<{
      label: string;
      data: number[];
      backgroundColor: string;
      type?: string;
      borderColor?: string;
      borderWidth?: number;
      borderDash?: number[];
      stack?: string;
      yAxisID?: string;
      tension?: number;
      pointRadius?: number;
      pointHoverRadius?: number;
      fill?: boolean;
    }>;
  } | null>(null);
  readonly distributionChartData = signal<{
    labels: string[];
    datasets: Array<{
      data: number[];
      backgroundColor: string[];
      hoverBackgroundColor: string[];
    }>;
  } | null>(null);

  readonly refreshChartMenuItems: MenuItem[] = [
    { label: 'Actualizar gráfico', icon: 'pi pi-refresh', command: () => this.loadDashboard() },
  ];

  readonly integrationsMenuItems: MenuItem[] = [
    { label: 'Actualizar tabla', icon: 'pi pi-refresh', command: () => this.loadDashboard() },
    { label: 'Limpiar filtros', icon: 'pi pi-filter-slash', command: () => this.clearFilters() },
  ];

  readonly volumeChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          usePointStyle: true,
          padding: 18,
        },
      },
      tooltip: {
        callbacks: {
          footer: (items: Array<{ dataIndex: number }>) => {
            const index = items[0]?.dataIndex ?? 0;
            const total = this.dashboardData()?.volume[index]?.total ?? 0;
            return `Total: ${total}`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          color: 'rgba(148, 163, 184, 0.12)',
        },
        ticks: {
          color: '#64748b',
          maxRotation: 0,
          autoSkipPadding: 12,
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: {
          color: 'rgba(148, 163, 184, 0.12)',
        },
        ticks: {
          color: '#64748b',
        },
      },
      y1: {
        position: 'right' as const,
        beginAtZero: true,
        max: 100,
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          color: '#334155',
          callback: (value: string | number) => `${value}%`,
        },
      },
    },
  };

  readonly doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          usePointStyle: true,
          padding: 16,
        },
      },
    },
  };

  readonly rangeOptions = [
    { label: 'Últimos 30 días', value: '30d' },
    { label: 'Últimos 60 días', value: '60d' },
    { label: 'Últimos 90 días', value: '90d' },
    { label: 'Últimos 120 días', value: '120d' },
    { label: 'Últimos 150 días', value: '150d' },
    { label: 'Últimos 180 días', value: '180d' },
  ];

  readonly statusOptions = [
    { label: 'Todos los estados', value: null, icon: 'pi pi-filter' },
    { label: 'Éxito', value: 'success', icon: 'pi pi-check-circle' },
    { label: 'Error', value: 'error', icon: 'pi pi-times-circle' },
    { label: 'Omitida', value: 'skipped', icon: 'pi pi-minus-circle' },
    { label: 'Procesando', value: 'processing', icon: 'pi pi-spin pi-spinner' },
    { label: 'Redirigida', value: 'redirected', icon: 'pi pi-arrow-right-arrow-left' },
  ];

  readonly workflowOptions = signal<WorkflowOption[]>([]);
  readonly selectedRange = signal('30d');
  readonly selectedStatus = signal<string | null>(null);
  readonly workflowId = signal<number | null>(null);

  integrationRows = 10;
  readonly integrationRowsPerPageOptions = [5, 10, 20, 50];

  readonly activeFiltersCount = computed(() => {
    let count = 0;
    if (this.selectedRange() !== '30d') count++;
    if (this.selectedStatus()) count++;
    if (this.workflowId() !== null) count++;
    return count;
  });

  readonly hasData = computed(() => {
    const data = this.dashboardData();
    if (!data) return false;
    return (
      data.summary.total_transactions > 0 ||
      data.integrations.length > 0 ||
      data.volume.length > 0 ||
      data.distribution.distribution.length > 0
    );
  });

  readonly selectedStatusOption = computed(
    () =>
      this.statusOptions.find((o) => o.value === this.selectedStatus()) ?? this.statusOptions[0],
  );

  private static readonly STATUS_LABEL_MAP: Record<string, string> = {
    success: 'Éxito',
    ok: 'OK',
    error: 'Error',
    omitted: 'Omitida',
    skipped: 'Omitida',
    processing: 'Procesando',
    redirected: 'Redirigida',
  };

  private static readonly STATUS_CLASS_MAP: Record<string, string> = {
    success:
      'border-emerald-200 bg-emerald-100 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
    ok: 'border-emerald-200 bg-emerald-100 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
    error:
      'border-red-200 bg-red-100 text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300',
    processing:
      'border-blue-200 bg-blue-100 text-blue-700 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    redirected:
      'border-violet-200 bg-violet-100 text-violet-700 dark:border-violet-800 dark:bg-violet-900/30 dark:text-violet-300',
  };

  private static readonly DEFAULT_STATUS_CLASS =
    'border-amber-200 bg-amber-100 text-amber-700 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300';

  private static readonly DISTRIBUTION_COLOR_MAP: Record<string, { bg: string; hover: string }> = {
    success: { bg: '#22c55e', hover: '#16a34a' },
    ok: { bg: '#22c55e', hover: '#16a34a' },
    error: { bg: '#ef4444', hover: '#dc2626' },
    omitted: { bg: '#f59e0b', hover: '#d97706' },
    skipped: { bg: '#f59e0b', hover: '#d97706' },
    processing: { bg: '#3b82f6', hover: '#2563eb' },
    redirected: { bg: '#8b5cf6', hover: '#7c3aed' },
  };

  private readonly breadcrumbNav = inject(BreadcrumbNavigationService);
  readonly displayBreadcrumbs = computed(() => this.breadcrumbNav.generateBreadcrumbs('dashboard'));
  private readonly dashboardService = inject(DashboardService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly menuHideTimeouts = new Map<Menu, ReturnType<typeof setTimeout>>();
  private readonly menuListeners = new Map<
    HTMLElement,
    { enter: EventListener; leave: EventListener }
  >();

  ngOnInit(): void {
    this.dashboardService
      .getWorkflows()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((workflows) => this.workflowOptions.set(workflows));

    this.loadDashboard();
  }

  ngOnDestroy(): void {
    this.menuHideTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.menuHideTimeouts.clear();
    this.menuListeners.forEach(({ enter, leave }, container) => {
      container.removeEventListener('mouseenter', enter);
      container.removeEventListener('mouseleave', leave);
    });
    this.menuListeners.clear();
  }

  loadDashboard(): void {
    this.loading.set(true);
    this.errorMessage.set('');

    this.dashboardService
      .getDashboardSummary({
        range: this.selectedRange(),
        workflow_id: this.workflowId() ?? undefined,
        status: this.selectedStatus() || undefined,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.dashboardData.set(response);
          this.updateChartData(response);
          this.loading.set(false);
        },
        error: () => {
          this.errorMessage.set('No fue posible cargar el dashboard de integraciones.');
          this.loading.set(false);
        },
      });
  }

  clearFilters(): void {
    this.selectedRange.set('30d');
    this.selectedStatus.set(null);
    this.workflowId.set(null);
    this.loadDashboard();
  }

  onRangeChange(value: string): void {
    this.selectedRange.set(value);
    this.loadDashboard();
  }

  onStatusChange(value: string | null): void {
    this.selectedStatus.set(value);
    this.loadDashboard();
  }

  onWorkflowChange(value: number | null): void {
    this.workflowId.set(value);
    this.loadDashboard();
  }

  goToWorkflowMonitoring(workflowId: number): void {
    this.router.navigate(['/monitoreo/workflow', workflowId]).then((r) => r);
  }

  getStatusLabel(status: string): string {
    return DashboardComponent.STATUS_LABEL_MAP[status] ?? status;
  }

  getIntegrationStatusClass(status: string): string {
    return (
      DashboardComponent.STATUS_CLASS_MAP[(status || '').toLowerCase()] ??
      DashboardComponent.DEFAULT_STATUS_CLASS
    );
  }

  getTrendClass(tendencyPct: number | null): string {
    if (tendencyPct !== null && tendencyPct > 0) {
      return 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300';
    }
    if (tendencyPct !== null && tendencyPct < 0) {
      return 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300';
    }
    return 'border-gray-200 bg-gray-100 text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300';
  }

  openCardMenu(event: MouseEvent, menu: Menu): void {
    this.clearCardMenuHide(menu);
    menu.show(event);
  }

  scheduleCardMenuHide(menu: Menu, event?: MouseEvent): void {
    const nextTarget = event?.relatedTarget;
    const menuContainer = menu.container as HTMLElement | undefined;

    if (nextTarget instanceof Node && menuContainer?.contains(nextTarget)) {
      return;
    }

    this.clearCardMenuHide(menu);

    const timeoutId = setTimeout(() => {
      const triggerHovered =
        event?.currentTarget instanceof Element && event.currentTarget.matches(':hover');
      const menuHovered = Boolean(menuContainer?.matches(':hover'));

      if (!triggerHovered && !menuHovered) {
        menu.hide();
      }
    }, 120);

    this.menuHideTimeouts.set(menu, timeoutId);
  }

  bindCardMenuHover(menu: Menu): void {
    const menuContainer = menu.container as HTMLElement | undefined;

    if (!menuContainer || this.menuListeners.has(menuContainer)) {
      return;
    }

    const enter: EventListener = () => this.clearCardMenuHide(menu);
    const leave: EventListener = (e) => this.scheduleCardMenuHide(menu, e as MouseEvent);

    menuContainer.addEventListener('mouseenter', enter);
    menuContainer.addEventListener('mouseleave', leave);
    this.menuListeners.set(menuContainer, { enter, leave });
  }

  private clearCardMenuHide(menu: Menu): void {
    const timeoutId = this.menuHideTimeouts.get(menu);

    if (timeoutId) {
      clearTimeout(timeoutId);
      this.menuHideTimeouts.delete(menu);
    }
  }

  private updateChartData(response: DashboardSummaryResponse): void {
    const volume = response.volume.reduce(
      (acc, item) => {
        acc.labels.push(item.label);
        acc.success.push(item.success);
        acc.failed.push(item.failed);
        acc.skipped.push(item.skipped);
        acc.processing.push(item.processing);
        acc.redirected.push(item.redirected);
        acc.errorRate.push(
          item.total > 0 ? Number(((item.failed / item.total) * 100).toFixed(1)) : 0,
        );
        return acc;
      },
      {
        labels: [] as string[],
        success: [] as number[],
        failed: [] as number[],
        skipped: [] as number[],
        processing: [] as number[],
        redirected: [] as number[],
        errorRate: [] as number[],
      },
    );

    this.volumeChartData.set({
      labels: volume.labels,
      datasets: [
        {
          label: 'Éxito',
          data: volume.success,
          backgroundColor: '#22c55e',
          borderColor: '#16a34a',
          borderWidth: 1,
          stack: 'volume',
        },
        {
          label: 'Error',
          data: volume.failed,
          backgroundColor: '#ef4444',
          borderColor: '#dc2626',
          borderWidth: 1,
          stack: 'volume',
        },
        {
          label: 'Omitida',
          data: volume.skipped,
          backgroundColor: '#f59e0b',
          borderColor: '#d97706',
          borderWidth: 1,
          stack: 'volume',
        },
        {
          label: 'Procesando',
          data: volume.processing,
          backgroundColor: '#3b82f6',
          borderColor: '#2563eb',
          borderWidth: 1,
          stack: 'volume',
        },
        {
          label: 'Redirigida',
          data: volume.redirected,
          backgroundColor: '#8b5cf6',
          borderColor: '#7c3aed',
          borderWidth: 1,
          stack: 'volume',
        },
        {
          type: 'line',
          label: '% error',
          data: volume.errorRate,
          // Gris oscuro: en ambar se confundia con las barras de "Omitida".
          backgroundColor: '#334155',
          borderColor: '#334155',
          borderDash: [4, 4],
          yAxisID: 'y1',
          tension: 0.35,
          pointRadius: 3,
          pointHoverRadius: 5,
          fill: false,
        },
      ],
    });

    const dist = response.distribution.distribution.reduce(
      (acc, item) => {
        const color = DashboardComponent.DISTRIBUTION_COLOR_MAP[item.status];
        acc.labels.push(this.getStatusLabel(item.status));
        acc.data.push(item.count);
        acc.bg.push(color?.bg ?? '#94a3b8');
        acc.hover.push(color?.hover ?? '#64748b');
        return acc;
      },
      {
        labels: [] as string[],
        data: [] as number[],
        bg: [] as string[],
        hover: [] as string[],
      },
    );

    this.distributionChartData.set({
      labels: dist.labels,
      datasets: [
        {
          data: dist.data,
          backgroundColor: dist.bg,
          hoverBackgroundColor: dist.hover,
        },
      ],
    });
  }
}
